import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, mergeProps as _mergeProps, createBlock as _createBlock, vModelText as _vModelText, withDirectives as _withDirectives, Fragment as _Fragment, normalizeStyle as _normalizeStyle, renderList as _renderList, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "flex items-start justify-between gap-3" }
const _hoisted_2 = { class: "inline-flex text-xs font-semibold rounded-full px-2.5 py-1 bg-gray-100 text-gray-700" }
const _hoisted_3 = { class: "text-lg font-bold text-gray-900 mt-3 tracking-tight" }
const _hoisted_4 = {
  key: 1,
  class: "mt-3"
}
const _hoisted_5 = {
  "data-sms-count": "",
  class: "text-xs text-gray-500 mt-1"
}
const _hoisted_6 = {
  key: 2,
  class: "mt-3 text-sm text-amber-900 bg-amber-50 rounded-xl px-3 py-2"
}
const _hoisted_7 = { class: "mt-3 h-1.5 bg-gray-200 rounded-full overflow-hidden" }
const _hoisted_8 = { class: "text-xs text-gray-500 mt-2" }
const _hoisted_9 = { class: "mt-3 text-sm text-gray-700 list-disc pl-5 space-y-1" }
const _hoisted_10 = { class: "mt-4 flex flex-wrap items-center justify-between gap-3" }
const _hoisted_11 = { class: "text-xs text-gray-500" }
const _hoisted_12 = {
  key: 0,
  class: "flex gap-2"
}
const _hoisted_13 = {
  key: 1,
  class: "flex items-center gap-2"
}
const _hoisted_14 = {
  key: 2,
  class: "flex gap-2"
}

import { computed, ref, watch } from 'vue'

import NeueButton from '@/components/neue/NeueButton.vue'

import MarketingPlayTimeline from './MarketingPlayTimeline.vue'
import {
  cardState,
  COMPLIANCE_RULES,
  formatDay,
  formatReturn,
  formatWhen,
  kitSteps,
  laneLabel,
  money,
  smsSegmentCount,
  timelineFor
} from './marketingSeries'
import MarketingStatRow from './MarketingStatRow.vue'

const KIT_PREVIEW_STEPS = 2
const PROPOSED_KIT_STEPS = 5


export default {
  __name: 'MarketingPlayCard',
  props: {
  item: { type: Object, required: true },
  busy: { type: Boolean, default: false },
  recipients: { type: Number, default: null }
},
  emits: ['approve', 'edit', 'skip', 'cancel', 'redraft', 'done'],
  setup(__props, { emit: __emit }) {

const props = __props
const emit = __emit

const AI_VISIBILITY_KEYS = [
  'own-your-google-business-profile',
  'ask-every-happy-customer-for-a-review',
  'reply-to-every-review'
]
const attrs = computed(() => props.item.attributes || {})
const isKit = computed(() => !attrs.value.sms_eligible)
const state = computed(() => cardState(props.item))
const timeline = computed(() => timelineFor(props.item))
const title = computed(() => props.item.play?.attributes?.title || attrs.value.play_title)
const laneTag = computed(() => `${laneLabel(attrs.value.lane)} · ${isKit.value ? 'Kit' : 'Text'}`)
const whyNow = computed(() => attrs.value.copy?.why_now || attrs.value.reason || '')
// why_now is usually a full sentence already; drop its full stop before we add our own.
const withoutFullStop = text => text.replace(/\.\s*$/, '')

const steps = computed(() => kitSteps(props.item))
const showAllSteps = ref(false)
const visibleSteps = computed(() =>
  showAllSteps.value
    ? steps.value
    : steps.value.slice(0, state.value.key === 'decide_kit' ? PROPOSED_KIT_STEPS : KIT_PREVIEW_STEPS)
)

const progressPct = computed(() =>
  attrs.value.recipients_count ? Math.round(((attrs.value.sent_count || 0) / attrs.value.recipients_count) * 100) : 0
)
const daysLeft = computed(() =>
  attrs.value.code_expires_at
    ? Math.max(0, Math.ceil((new Date(attrs.value.code_expires_at) - Date.now()) / 86400000))
    : null
)
const codeLine = computed(() =>
  attrs.value.discount_code
    ? `Code ${attrs.value.discount_code}${daysLeft.value !== null ? ` · ${daysLeft.value} days left` : ''}`
    : ''
)
const noCodeSentence = computed(() =>
  AI_VISIBILITY_KEYS.includes(attrs.value.play_key)
    ? 'No code on this one. We check whether AI assistants name you, nightly.'
    : 'No code on this one, so orders are not tracked.'
)

const failureSentence = computed(() => {
  const lines = (attrs.value.compliance?.failures || []).map(key => COMPLIANCE_RULES[key] || key)
  const lead = lines.length === 1 ? 'One thing to fix: ' : `${lines.length} things to fix: `
  return `${lead}${lines.join(' ')} Shorten it or let us redraft.`
})

const resultStats = computed(() => {
  const a = attrs.value
  const orders = { label: 'Orders', value: a.orders_count || 0 }
  const revenue = { label: 'Revenue', value: money(a.revenue_cents) }
  const ret = { label: 'Return', value: formatReturn(a.return_multiple), muted: a.return_multiple === null }
  if (isKit.value) return [orders, revenue, ret]
  return [
    { label: 'Delivered', value: a.sent_count || 0 },
    { label: 'Replied', value: a.replies_count || 0 },
    orders,
    revenue,
    ret
  ]
})

const closedSentence = a => {
  if (a.status === 'skipped')
    return `Skipped on ${formatDay(a.completed_at)}${a.skip_reason ? `: "${a.skip_reason}"` : ''}`
  if (a.status === 'expired') return 'Expired unsent at month end'
  if (a.status === 'failed') return `${a.failed_count || 0} texts failed · we tried twice`
  return ''
}

const footerSentence = computed(() => {
  const a = attrs.value
  switch (state.value.key) {
    case 'decide': {
      const who =
        props.recipients === null
          ? 'your customers who said yes to texts'
          : `${props.recipients} customers who said yes to texts`
      return `Goes to ${who} · next slot ${formatWhen(a.next_send_at)} · we add your order link and the STOP line. Why now: ${whyNow.value}`
    }
    case 'decide_kit':
      return a.discount_code
        ? `Why now: ${withoutFullStop(whyNow.value)}. Approving creates code ${a.discount_code} for the flyer. Orders using it count here.`
        : `Why now: ${whyNow.value}`
    case 'scheduled':
      return [
        `${a.recipients_count || props.recipients || 0} recipients`,
        a.discount_code && a.code_expires_at ? `code live until ${formatDay(a.code_expires_at)}` : ''
      ]
        .filter(Boolean)
        .join(' · ')
    case 'results':
      return [codeLine.value, 'updated nightly'].filter(Boolean).join(' · ')
    case 'doing':
      return a.discount_code ? codeLine.value : noCodeSentence.value
    case 'done_unmeasured':
      return noCodeSentence.value
    case 'closed':
      return closedSentence(a)
    default:
      return ''
  }
})

const editing = ref(false)
const smsDraft = ref('')
const skipping = ref(false)
const skipReason = ref('')
const startEdit = () => {
  smsDraft.value = attrs.value.copy?.sms || ''
  editing.value = true
}

const count = computed(() => smsSegmentCount(smsDraft.value))

// The parent swaps in a new item only after a confirmed server update, so a failed save
// leaves the editor open with the merchant's text.
watch(
  () => props.item,
  () => {
    editing.value = false
    skipping.value = false
  }
)

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("article", {
    "data-card": "",
    class: _normalizeClass(["bg-white rounded-2xl border p-5", [
      state.value.hot ? 'border-amber-400 ring-[3px] ring-amber-100' : 'border-gray-200',
      state.value.dim ? 'opacity-60' : ''
    ]])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(laneTag.value), 1),
      _createElementVNode("span", {
        class: _normalizeClass(["shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1", state.value.pill.classes])
      }, [
        (state.value.pill.dot || state.value.pill.pulse)
          ? (_openBlock(), _createElementBlock("span", {
              key: 0,
              class: _normalizeClass(["h-1.5 w-1.5 rounded-full bg-current", state.value.pill.pulse ? 'animate-pulse' : '']),
              "aria-hidden": "true"
            }, null, 2))
          : _createCommentVNode("", true),
        _createTextVNode(" " + _toDisplayString(state.value.pill.label), 1)
      ], 2)
    ]),
    _createElementVNode("h3", _hoisted_3, _toDisplayString(title.value), 1),
    (state.value.key !== 'closed')
      ? (_openBlock(), _createBlock(MarketingPlayTimeline, _mergeProps({
          key: 0,
          class: "mt-2"
        }, timeline.value), null, 16))
      : _createCommentVNode("", true),
    (['decide', 'scheduled', 'needs_edit'].includes(state.value.key))
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (!editing.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                "data-sms": "",
                class: _normalizeClass(["mt-3 rounded-xl px-4 py-3 text-sm text-gray-900 whitespace-pre-wrap", state.value.key === 'needs_edit' ? 'bg-red-50 border border-red-200' : 'bg-gray-100'])
              }, _toDisplayString(attrs.value.copy?.sms), 3))
            : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _withDirectives(_createElementVNode("textarea", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((smsDraft).value = $event)),
                  "data-sms-editor": "",
                  "aria-label": "Text message",
                  rows: "3",
                  class: "w-full text-sm border border-gray-300 rounded-xl px-3 py-2"
                }, null, 512), [
                  [_vModelText, smsDraft.value]
                ]),
                _createElementVNode("p", _hoisted_5, _toDisplayString(count.value.chars) + " of " + _toDisplayString(count.value.limit) + " characters · " + _toDisplayString(count.value.segments) + " text " + _toDisplayString(count.value.segments === 1 ? 'segment' : 'segments') + ". We add the order link and \"Reply STOP to opt out.\" when it sends. ", 1)
              ]))
        ], 64))
      : _createCommentVNode("", true),
    (state.value.key === 'needs_edit')
      ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(failureSentence.value), 1))
      : _createCommentVNode("", true),
    (state.value.key === 'sending')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", {
              "data-progress": "",
              class: "h-1.5 bg-blue-700 rounded-full",
              style: _normalizeStyle({ width: `${progressPct.value}%` })
            }, null, 4)
          ]),
          _createElementVNode("p", _hoisted_8, " Up to " + _toDisplayString(attrs.value.daily_send_cap) + " a day, inside " + _toDisplayString(attrs.value.send_window_start) + ":00 to " + _toDisplayString(attrs.value.send_window_end) + ":00. The rest go tomorrow. ", 1)
        ], 64))
      : _createCommentVNode("", true),
    (state.value.key === 'results')
      ? (_openBlock(), _createBlock(MarketingStatRow, {
          key: 4,
          class: "mt-4",
          stats: resultStats.value
        }, null, 8, ["stats"]))
      : _createCommentVNode("", true),
    (isKit.value && ['decide_kit', 'doing', 'done_unmeasured', 'results'].includes(state.value.key))
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
          _createElementVNode("ul", _hoisted_9, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleSteps.value, (step) => {
              return (_openBlock(), _createElementBlock("li", { key: step }, _toDisplayString(step), 1))
            }), 128))
          ]),
          (steps.value.length > visibleSteps.value.length)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                class: "text-xs underline text-gray-600 mt-1",
                onClick: _cache[1] || (_cache[1] = $event => (showAllSteps.value = true))
              }, " Show all " + _toDisplayString(steps.value.length) + " steps ", 1))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("p", _hoisted_11, _toDisplayString(footerSentence.value), 1),
      (editing.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
            _createVNode(NeueButton, {
              "data-save": "",
              disabled: __props.busy,
              onClick: _cache[2] || (_cache[2] = $event => (emit('edit', { sms: smsDraft.value })))
            }, {
              default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                _createTextVNode("Save", -1)
              ]))]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(NeueButton, {
              type: "secondary",
              "data-cancel-edit": "",
              disabled: __props.busy,
              onClick: _cache[3] || (_cache[3] = $event => (editing.value = false))
            }, {
              default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                _createTextVNode("Cancel", -1)
              ]))]),
              _: 1
            }, 8, ["disabled"])
          ]))
        : (skipping.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[4] || (_cache[4] = $event => ((skipReason).value = $event)),
                "data-skip-reason": "",
                "aria-label": "Reason for skipping",
                placeholder: "Why skip it? (optional)",
                class: "text-sm border border-gray-300 rounded-lg px-2 py-1"
              }, null, 512), [
                [_vModelText, skipReason.value]
              ]),
              _createVNode(NeueButton, {
                "data-skip-confirm": "",
                disabled: __props.busy,
                onClick: _cache[5] || (_cache[5] = $event => (emit('skip', skipReason.value)))
              }, {
                default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                  _createTextVNode("Skip it", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(NeueButton, {
                type: "secondary",
                disabled: __props.busy,
                onClick: _cache[6] || (_cache[6] = $event => (skipping.value = false))
              }, {
                default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                  _createTextVNode("Keep", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_14, [
              (state.value.key === 'decide' || state.value.key === 'decide_kit')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createVNode(NeueButton, {
                      "data-approve": "",
                      disabled: __props.busy || !attrs.value.compliance?.ok,
                      onClick: _cache[7] || (_cache[7] = $event => (emit('approve')))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(isKit.value ? 'I will do this' : 'Approve and send'), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"]),
                    (!isKit.value)
                      ? (_openBlock(), _createBlock(NeueButton, {
                          key: 0,
                          type: "secondary",
                          "data-edit": "",
                          disabled: __props.busy,
                          onClick: startEdit
                        }, {
                          default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                            _createTextVNode("Edit", -1)
                          ]))]),
                          _: 1
                        }, 8, ["disabled"]))
                      : _createCommentVNode("", true),
                    _createVNode(NeueButton, {
                      type: "secondary",
                      "data-skip": "",
                      disabled: __props.busy,
                      onClick: _cache[8] || (_cache[8] = $event => (skipping.value = true))
                    }, {
                      default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                        _createTextVNode("Skip", -1)
                      ]))]),
                      _: 1
                    }, 8, ["disabled"])
                  ], 64))
                : (state.value.key === 'needs_edit')
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createVNode(NeueButton, {
                        "data-edit": "",
                        disabled: __props.busy,
                        onClick: startEdit
                      }, {
                        default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                          _createTextVNode("Edit", -1)
                        ]))]),
                        _: 1
                      }, 8, ["disabled"]),
                      _createVNode(NeueButton, {
                        type: "secondary",
                        "data-redraft": "",
                        disabled: __props.busy,
                        onClick: _cache[9] || (_cache[9] = $event => (emit('redraft')))
                      }, {
                        default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                          _createTextVNode(" Redraft for me ", -1)
                        ]))]),
                        _: 1
                      }, 8, ["disabled"]),
                      _createVNode(NeueButton, {
                        type: "secondary",
                        "data-skip": "",
                        disabled: __props.busy,
                        onClick: _cache[10] || (_cache[10] = $event => (skipping.value = true))
                      }, {
                        default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                          _createTextVNode("Skip", -1)
                        ]))]),
                        _: 1
                      }, 8, ["disabled"])
                    ], 64))
                  : (state.value.key === 'scheduled')
                    ? (_openBlock(), _createBlock(NeueButton, {
                        key: 2,
                        type: "secondary",
                        "data-cancel": "",
                        disabled: __props.busy,
                        onClick: _cache[11] || (_cache[11] = $event => (emit('cancel')))
                      }, {
                        default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                          _createTextVNode(" Cancel send ", -1)
                        ]))]),
                        _: 1
                      }, 8, ["disabled"]))
                    : (isKit.value && attrs.value.status === 'approved')
                      ? (_openBlock(), _createBlock(NeueButton, {
                          key: 3,
                          type: "secondary",
                          "data-done": "",
                          disabled: __props.busy,
                          onClick: _cache[12] || (_cache[12] = $event => (emit('done')))
                        }, {
                          default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                            _createTextVNode(" Mark done ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["disabled"]))
                      : _createCommentVNode("", true)
            ]))
    ])
  ], 2))
}
}

}